import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import { Close, Error, Wifi } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Typography } from '@mui/material'
import { addMinutes, isBefore } from 'date-fns'
import { Lottie } from 'lottie-react'
import { useSnackbar } from 'notistack'

import pulseAnimation from '../../animations/pulse.json'
import AnimatedCheck from '../../components/animated-check'
import BackButton from '../../components/back-button'
import ExitActivationModal from '../../components/exit-activation-modal'
import ScreenContainer from '../../components/screen'
import SensorActivationFooter from '../../components/sensor-activation-footer'
import lfSensorImage from '../../images/leak-freeze-activation/device-connection/leak-freeze.svg'
import { useActivationContext } from '../../providers/activation-provider'
import Api from '../../utils/api'
import useWebsocket from '../../utils/hooks/useWebsocket'

import classes from './index.styles'
import TroubleshootingModal from './troubleshooting-modal'

const DeviceConnection = () => {
  const navigate = useNavigate()
  const { activationLocationState } = useActivationContext()
  const { nodeTypeName, nodeId } = activationLocationState
  const { enqueueSnackbar } = useSnackbar()
  const [lastCheckIn, setLastCheckIn] = useState<string | null>(null)
  const [hasLowSignal, setHasLowSignal] = useState<boolean>(false)
  const [connectionTimeout, setConnectionTimeout] = useState<boolean>(false)
  const [leakFreezeLongWait, setLeakFreezeLongWait] = useState<boolean>(false)
  const [animateCheck, setAnimateCheck] = useState<boolean>(false)
  const [showExitActivationDialog, setShowExitActivationDialog] = useState<boolean>(false)
  const [showTroubleshootingModal, setShowTroubleshootingModal] = useState<boolean>(false)

  const isSearching = useMemo(() => {
    return !lastCheckIn || (!!lastCheckIn && isBefore(addMinutes(new Date(lastCheckIn), 15), new Date()))
  }, [lastCheckIn]) // lastCheckIn must have been within the last 15 minutes

  // If connection is successful, show an animated checkmark for 2 seconds before showing the connected status
  useEffect(() => {
    if (!isSearching) {
      setAnimateCheck(true)
      setTimeout(() => {
        setAnimateCheck(false)
      }, 2000)
    }
  }, [isSearching])

  const fetchData = useCallback(async () => {
    if (!nodeId) return
    try {
      const res = await Api.get(`/api/v2/protect/devices/${nodeId}`)
      if (!res.ok) throw Error
      setLastCheckIn(res.body.lastCheckIn)
      setHasLowSignal(res.body.hasLowSignal)
    } catch {
      enqueueSnackbar('Error fetching device data. Please try again.', { variant: 'error' })
    }
  }, [enqueueSnackbar, nodeId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useWebsocket({
    subscribeIds: nodeId ? [nodeId] : [],
    onMessage: (message) => {
      setLastCheckIn(new Date().toISOString()) // We received a message, so current time is our last check in
      if (message.channelName === 'signal_indicator' && Number(message.value) < 2) setHasLowSignal(true) // If signal indicator is less than 2, we consider that low signal and show the warning to the user on the UI
      if (message.channelName === 'signal_indicator' && Number(message.value) >= 2) setHasLowSignal(false) // If signal indicator is 2 or above, we do not show the low signal warning to the user on the UI
    },
    shouldBeAlive: true,
  })

  // When data is received and the device is now connected, reset the connection timeout and leak freeze long wait states
  useEffect(() => {
    if (!isSearching) {
      setConnectionTimeout(false)
      setLeakFreezeLongWait(false)
      localStorage.removeItem('connectionStartTime')
    }
  }, [isSearching])

  useEffect(() => {
    if (isSearching) {
      const timeoutTime = 60000 + 90000 // First 60 seconds + additional extended 90 seconds where we tell user to keep waiting
      const startTime = localStorage.getItem('connectionStartTime')

      let elapsedTime = 0
      if (startTime) {
        elapsedTime = Date.now() - parseInt(startTime, 10)
      } else {
        localStorage.setItem('connectionStartTime', Date.now().toString())
      }

      const remainingTimeoutTime = Math.max(timeoutTime - elapsedTime, 0)
      const remainingLongWaitTime = Math.max(60000 - elapsedTime, 0)

      // Set a connection timeout if the device has not communicated within the allotted time
      const timeoutId = setTimeout(() => {
        if (isSearching) setConnectionTimeout(true)
      }, remainingTimeoutTime)
      // Set a 60 second timeout to let the user know that leak & freeze connection is taking longer than expected
      const longWaitId = setTimeout(() => {
        if (isSearching) setLeakFreezeLongWait(true)
      }, remainingLongWaitTime)

      return () => {
        clearTimeout(timeoutId)
        clearTimeout(longWaitId)
      }
    }
    return () => {}
  }, [isSearching])

  useEffect(() => {
    // Create a 30 second interval where we ping the device endpoint to get the information we need if we haven't gotten a websocket message in that time
    const interval = setInterval(() => {
      fetchData()
    }, 30000)
    if (!isSearching) clearInterval(interval) // Stop the interval when we get a message from the device
    return () => clearInterval(interval)
  }, [isSearching, fetchData])

  if (nodeTypeName === 'nbiot_leak' && nodeId)
    return (
      <ScreenContainer
        useDarkBackground={true}
        headerProps={{
          title: 'Activate',
          showDownloadAppButton: false,
          leftIcon: <BackButton />,
          rightIcon: isSearching ? (
            <IconButton color="inherit" onClick={() => setShowExitActivationDialog(true)} aria-label="Exit activation">
              <Close />
            </IconButton>
          ) : null,
          useDarkBackground: true,
          progress: 50,
        }}
      >
        <AnimatedCheck isVisible={animateCheck} />

        <Grid container={true} sx={classes.mainContainer}>
          {!animateCheck && (
            <>
              <Box sx={classes.lfImageContainer}>
                {isSearching && !connectionTimeout && (
                  <Lottie src={pulseAnimation} autoplay={true} loop={true} data-testid="pulse-animation" style={classes.pulseAnimation} />
                )}
                <img src={lfSensorImage} alt="" width="147" height="147" style={classes.lfImage} />
              </Box>

              <Typography variant="body2" sx={[classes.statusLabel, classes.abovePulse]}>
                Status:
              </Typography>

              <Grid container={true} sx={[classes.statusContainer, classes.abovePulse]}>
                {isSearching && !connectionTimeout && !leakFreezeLongWait && (
                  <>
                    <Typography variant="h5" sx={classes.statusTitle}>
                      Searching for signal
                    </Typography>
                    <Typography variant="body2" sx={classes.statusMessage}>
                      This process may take up to 60 seconds
                    </Typography>
                  </>
                )}

                {isSearching && !connectionTimeout && leakFreezeLongWait && (
                  <>
                    <Typography variant="h5" sx={classes.statusTitle}>
                      Taking longer than expected
                    </Typography>
                    <Typography variant="body2" sx={classes.statusMessage}>
                      Your device is still attempting to connect. Please wait another 90 seconds.
                    </Typography>
                  </>
                )}

                {isSearching && connectionTimeout && (
                  <Grid container={true} sx={classes.errorContainer}>
                    <Error />
                    <Typography variant="h5">Unable to connect</Typography>
                  </Grid>
                )}

                {!isSearching && (
                  <>
                    <Grid container={true} sx={classes.connectedContainer}>
                      <Wifi />
                      <Typography variant="h5">Connected</Typography>
                    </Grid>
                    {hasLowSignal && (
                      <Grid container={true} sx={classes.lowSignalContainer}>
                        <Typography variant="body2" sx={classes.statusMessage}>
                          You may continue activation, but your device has low signal, which may affect battery life.
                        </Typography>
                        <Button variant="text" onClick={() => setShowTroubleshootingModal(true)}>
                          Learn more
                        </Button>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>

              {connectionTimeout && (
                <Button variant="outlined" size="large" onClick={() => setShowTroubleshootingModal(true)} sx={{ paddingX: 4 }}>
                  Troubleshoot
                </Button>
              )}
            </>
          )}
        </Grid>

        <ExitActivationModal showExitActivationDialog={showExitActivationDialog} setShowExitActivationDialog={setShowExitActivationDialog} />
        <TroubleshootingModal isOpen={showTroubleshootingModal} onClose={() => setShowTroubleshootingModal(false)} />

        <SensorActivationFooter
          primaryButton={
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              sx={{ paddingX: 4 }}
              disabled={isSearching || animateCheck}
              onClick={() => {
                localStorage.removeItem('connectionStartTime')
                navigate('/placement-suggestions', { state: activationLocationState })
              }}
            >
              Next
            </Button>
          }
          sxFooter={classes.footer}
        />
      </ScreenContainer>
    )
  return <Navigate to="-1" />
}

export default DeviceConnection
